// Customer local time for a lead: which US timezone the customer is in, and the time there now.
// Source order: the lead's ZIP code (3-digit ZIP prefix -> zone, no outside service), then the
// IANA zone the customer's browser reported (lead.browserInfo.timeZone). Nothing is guessed
// when neither is there.
//
// Display only. Abbreviations (PST/PDT, EST/EDT, ...) come from Intl, so DST switches by itself.
//
// Tests: node scripts/test-leadTimezone.mjs
import { useEffect, useState } from 'react';

const ET = 'America/New_York';
const CT = 'America/Chicago';
const MT = 'America/Denver';
const AZ = 'America/Phoenix'; // no DST
const PT = 'America/Los_Angeles';
const AK = 'America/Anchorage';
const HI = 'Pacific/Honolulu';
const PR = 'America/Puerto_Rico';
const VI = 'America/St_Thomas';
const GU = 'Pacific/Guam';

// [first prefix, last prefix, zone]. Later rows override earlier ones, so split-zone
// exceptions are listed after their state's main range. Military (APO/FPO) prefixes are absent.
const ZIP_RANGES = [
  [5, 5, ET],          // NY (Holtsville)
  [6, 7, PR], [9, 9, PR],
  [8, 8, VI],
  [10, 89, ET],        // New England, NJ
  [100, 339, ET],      // NY, PA, DE, DC, MD, VA, WV, NC, SC, GA, FL
  [341, 349, ET],      // FL (340 is military)
  [324, 325, CT],      // FL panhandle: Panama City, Pensacola
  [350, 369, CT],      // AL
  [370, 385, CT],      // TN (middle and west)
  [373, 374, ET], [376, 379, ET], // TN east: Chattanooga, Johnson City, Knoxville
  [386, 397, CT],      // MS
  [398, 399, ET],      // GA
  [400, 427, ET],      // KY
  [420, 424, CT],      // KY west: Paducah, Bowling Green, Owensboro, Henderson
  [430, 459, ET],      // OH
  [460, 479, ET],      // IN
  [463, 464, CT], [476, 477, CT], // IN: Gary area, Evansville area
  [480, 499, ET],      // MI
  [500, 528, CT],      // IA
  [530, 567, CT],      // WI, MN
  [569, 569, ET],      // DC
  [570, 576, CT], [577, 577, MT], // SD (Rapid City is Mountain)
  [580, 588, CT], [586, 586, MT], // ND (Dickinson is Mountain)
  [590, 599, MT],      // MT
  [600, 679, CT],      // IL, MO, KS
  [680, 692, CT], [693, 693, MT], // NE (Alliance/panhandle is Mountain)
  [700, 797, CT],      // LA, AR, OK, TX
  [798, 799, MT],      // TX: El Paso
  [800, 831, MT],      // CO, WY
  [832, 837, MT], [838, 838, PT], // ID (north Idaho is Pacific)
  [840, 847, MT],      // UT
  [850, 865, AZ],      // AZ
  [870, 884, MT],      // NM
  [885, 885, MT],      // TX: El Paso
  [889, 898, PT],      // NV
  [900, 961, PT],      // CA
  [967, 968, HI],      // HI
  [969, 969, GU],      // Guam
  [970, 978, PT], [979, 979, MT], // OR (Ontario/Malheur is Mountain)
  [980, 994, PT],      // WA
  [995, 999, AK],      // AK
];

const ZONE_BY_PREFIX = new Map();
ZIP_RANGES.forEach(([from, to, zone]) => {
  for (let p = from; p <= to; p += 1) ZONE_BY_PREFIX.set(p, zone);
});

// '90001', '90001-1234', ' 90001 ' -> '90001'. A 4-digit value is a ZIP that lost its leading
// zero (e.g. 2134 -> 02134). Anything else ('UNKNOWN', '', 'N/A') -> null.
export const normalizeZip = (zip) => {
  if (zip === null || zip === undefined) return null;
  const s = String(zip).trim();
  let m = s.match(/^(\d{5})(?:-?\d{4})?$/);
  if (m) return m[1];
  m = s.match(/^(\d{4})$/);
  return m ? `0${m[1]}` : null;
};

export const zoneFromZip = (zip) => {
  const z = normalizeZip(zip);
  return z ? ZONE_BY_PREFIX.get(Number(z.slice(0, 3))) || null : null;
};

const isValidZone = (zone) => {
  if (!zone || typeof zone !== 'string') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
};

// Browser zones are only used when they are in the Americas or Hawaii; anything else is a
// non-US browser and says nothing about where the customer's vehicle is.
const isAmericasZone = (zone) => /^America\//.test(zone) || zone === HI || zone === GU;

// lead -> { zone, source: 'zip' | 'browser', zip? } or null.
export const resolveLeadTimeZone = (lead) => {
  const zip = normalizeZip(lead?.personalInfo?.zipCode);
  const zipZone = zip ? zoneFromZip(zip) : null;
  if (zipZone) return { zone: zipZone, source: 'zip', zip };

  const browserZone = lead?.browserInfo?.timeZone;
  if (isValidZone(browserZone) && isAmericasZone(browserZone)) {
    return { zone: browserZone, source: 'browser' };
  }
  return null;
};

const fmtCache = new Map();
const formatterFor = (zone) => {
  if (!fmtCache.has(zone)) {
    fmtCache.set(zone, {
      time: new Intl.DateTimeFormat('en-US', { timeZone: zone, hour: 'numeric', minute: '2-digit', hour12: true }),
      abbr: new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' }),
    });
  }
  return fmtCache.get(zone);
};

// 'PST', 'EDT', 'AKST', 'HST', ... for that zone at that moment.
export const zoneAbbr = (zone, date = new Date()) => {
  const part = formatterFor(zone).abbr.formatToParts(date).find((p) => p.type === 'timeZoneName');
  return part ? part.value : '';
};

// '8:30 PM' in that zone.
export const zoneTime = (zone, date = new Date()) => formatterFor(zone).time.format(date);

// One color per US zone family, keyed off the abbreviation.
const ZONE_COLORS = [
  [/^AK/, '#2563eb'],          // Alaska
  [/^H[SD]T$/, '#059669'],     // Hawaii
  [/^P[SD]T$/, '#7c3aed'],     // Pacific
  [/^M[SD]T$/, '#d97706'],     // Mountain
  [/^C[SD]T$/, '#0891b2'],     // Central
  [/^E[SD]T$/, '#db2777'],     // Eastern
];
export const zoneColor = (abbr) => (ZONE_COLORS.find(([re]) => re.test(abbr)) || [null, '#6b7280'])[1];

// One shared timer for every card on the page, ticking on each minute boundary.
const listeners = new Set();
let timer = null;
const notify = () => listeners.forEach((fn) => fn(Date.now()));
const schedule = () => {
  timer = setTimeout(() => {
    notify();
    schedule();
  }, 60000 - (Date.now() % 60000) + 50);
};

export const useMinuteClock = (enabled = true) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return undefined;
    listeners.add(setNow);
    if (!timer) schedule();
    setNow(Date.now());
    return () => {
      listeners.delete(setNow);
      if (listeners.size === 0 && timer) {
        clearTimeout(timer);
        timer = null;
      }
    };
  }, [enabled]);
  return now;
};
